import React, { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { Field } from "core/field/FieldService";

import EditableText from "utils/edit/EditableText";
import { ChangeEvent, Event } from "utils/history/Event";

export type FieldRowProps = {
	index: number;
	field: Field;
	editable: boolean;
	selected: boolean;

	onSelect: (select: boolean) => void;
	onChange: (event: Event) => void;
};

export type FieldBaseRowProps = FieldRowProps & {
	children?: React.ReactNode;
};

export default function FieldBaseRow(props: FieldBaseRowProps): JSX.Element {
	const { t } = useTranslation();

	const [expanded, setExpanded] = useState(false);

	function updateFieldName(newName: string): void {
		if (newName !== "") {
			const event = new ChangeEvent("/name", props.field.name, newName);
			props.onChange(event);
		}
	}

	const toggleExpansion = useCallback(() => {
		setExpanded(prevState => !prevState);
	}, []);

	return (
		<>
			<TableRow sx={{ "& > td": { borderBottom: "unset" } }}>
				<TableCell align="center" padding="checkbox">
					<Checkbox
						disabled={!props.editable}
						checked={props.selected}
						onChange={(_event, checked) => props.onSelect(checked)}
					/>
				</TableCell>
				<TableCell>{props.index + 1}</TableCell>
				<TableCell padding="checkbox">
					{props.children && (
						<IconButton onClick={toggleExpansion}>
							{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
						</IconButton>
					)}
				</TableCell>
				<TableCell sx={{ width: "20ch" }}>
					{t(props.field.type)}
				</TableCell>
				<TableCell
					sx={{
						paddingTop: 0,
						paddingBottom: 0,
						alignContent: "center"
					}}>
					<EditableText
						value={props.field.name}
						editable={props.editable}
						onChange={updateFieldName}
					/>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell
					style={{ paddingBottom: 0, paddingTop: 0 }}
					colSpan={6}>
					<Collapse in={expanded} timeout="auto" unmountOnExit>
						<Box sx={{ margin: 1 }}>{props.children}</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}
